/**
 * The Build screen: place blocks against each other in the browser, and watch §4.2's
 * three rules do the work.
 *
 * **Nothing here decides whether a placement is legal.** Every candidate goes through
 * `placeBlock`, and what comes back is drawn: an accepted placement, or a refusal
 * carrying the rule's own sentence. That is §8.2 -- placement cannot go wrong, because
 * a position that mates nothing simply does not take and the status line says which
 * rule refused it. Snapping is the same call tried at the offsets `matingOrigin` says
 * would mate an open port, so there is no second placement implementation here to
 * disagree with the server at stage 5.
 *
 * The admin editor's `modules/sandbox.ts` answers the same question for a scratch board
 * in plan view, and is deliberately not imported: `admin-editor` is an app rather than
 * a shared package, so the arithmetic comes from `shared` and only the look and the
 * gestures are each app's own. The difference that matters is what is being built -- the
 * sandbox's board is scratch and never a document, while this one *is* a `DungeonLevel`.
 *
 * The header is §9.1's visible pacing in its cheapest form: the block count and the
 * running connected and sealed totals, read off the board this screen just drew. Threat
 * values are stage 6 and no block carries one yet, so the sequence is not reported.
 *
 * **What is built survives a reload.** Every change is written to `levelDraft.ts` and
 * restored in the constructor, because this board is a document rather than the
 * sandbox's scratch space. Copy, Paste and New are the rest of what a document needs
 * until it can be saved somewhere other players can reach it: Copy puts the level's own
 * JSON on the clipboard, and **every document arriving at this board goes through
 * `readLevel`** -- the restored draft as much as a paste -- so one this build cannot
 * read is refused with the reader's own sentence and leaves the board untouched.
 */
import type { Scene } from '@babylonjs/core/scene';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import type { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { PointerEventTypes, type PointerInfo } from '@babylonjs/core/Events/pointerEvents';
import type { Observer } from '@babylonjs/core/Misc/observable';
import {
  TILE_SIZE,
  createEmptyLevel,
  matingOrigin,
  orientBlock,
  placeBlock,
  readLevel,
  resolveLevel,
  resolvePlacement,
  sealedPorts,
  tileToWorld,
  worldToTile,
  type BlockCatalogue,
  type BlockId,
  type BlockRotation,
  type Cell,
  type DungeonLevel,
  type PlacedBlock,
  type PlacementResult,
  type ResolvedBlock,
} from '@dungeon-builder/shared';

import type { DungeonView } from '../world/dungeonView.ts';
import { BlockPalette } from './blockPalette.ts';
import { loadDraft, saveDraft } from './levelDraft.ts';

/** The ground a dungeon may be built on, in cells. Room for a short run and its branches. */
const BOARD = { width: 15, depth: 11 } as const;

/** Looking down on the whole board rather than over a character's shoulder. */
const BOARD_FRAMING = { alpha: -Math.PI / 2, beta: Math.PI / 3.6, radius: 95 } as const;

/**
 * How far from the cursor a mating offset may be and still be the one snapped to, in
 * world units.
 *
 * Two cells, the same reach the editor's sandbox uses. Nearer than that and the builder
 * is plainly aiming at the port; further and yanking the block across the board would
 * place something they did not point at -- and a drop that mates nothing is a refusal
 * worth seeing, because it is rule 2.
 */
const SNAP_RADIUS = TILE_SIZE * 2;

const GRID_COLOUR = new Color3(0.17, 0.16, 0.22);

/** What the screen draws, and what the browser suite reads. */
export interface BuildState {
  placed: PlacedBlock[];
  /** Ports with a block across them -- derived, never stored (§4.2). */
  connections: number;
  /** Ports with nothing across them: rule 3's walls. */
  sealed: number;
  rotation: BlockRotation;
  mirrored: boolean;
  selected: BlockId | null;
  status: string;
}

export interface BuildScreenOptions {
  scene: Scene;
  camera: ArcRotateCamera;
  catalogue: BlockCatalogue;
  /** The view that draws the board and the block in hand. */
  view: DungeonView;
  /** Where the palette is rendered. */
  host: HTMLElement;
  /** The whole screen's chrome, hidden when the player leaves to play. */
  root: HTMLElement;
  header: {
    blocks: HTMLElement | null;
    connections: HTMLElement | null;
    sealed: HTMLElement | null;
  };
  /**
   * The three controls that act on the whole document rather than on one placement.
   *
   * Passed in and wired here, like the header's counters, because each of them is about
   * the level this screen holds. `#build-play` is the other way round and stays in
   * `main.ts`: pressing it leaves this screen entirely.
   */
  actions: {
    copy: HTMLElement | null;
    paste: HTMLElement | null;
    reset: HTMLElement | null;
  };
  status: HTMLElement | null;
}

export class BuildScreen {
  private level: DungeonLevel = createEmptyLevel();
  private instances = 0;

  private selected: BlockId | null = null;
  private rotation: BlockRotation = 0;
  private mirrored = false;

  /** Only the showing screen answers picks and keys: the scene is shared with the game. */
  private active = false;

  /** The candidate under the cursor, and what the rules said about it. */
  private candidate: { block: PlacedBlock; result: PlacementResult } | null = null;
  /** What was last reported, so a pointer moving inside one cell does not restate it. */
  private reported = '';
  private statusText = '';

  private readonly palette: BlockPalette;
  private readonly pickPlane: Mesh;
  private grid: LinesMesh | undefined;

  private readonly pointer: Observer<PointerInfo> | null;
  private readonly keys: (event: KeyboardEvent) => void;
  /** The header's own controls, kept so `dispose` can take its listeners back off. */
  private readonly clicks: { element: HTMLElement; handler: () => void }[] = [];

  constructor(private readonly options: BuildScreenOptions) {
    this.palette = new BlockPalette(options.host, {
      catalogue: options.catalogue,
      onPick: (id) => this.take(id),
    });

    // Zero visibility rather than `isVisible = false`: Babylon's default pick
    // predicate skips invisible meshes, so an invisible plane is never hit.
    this.pickPlane = MeshBuilder.CreateGround(
      'build-pick-plane',
      { width: 1, height: 1 },
      options.scene,
    );
    this.pickPlane.visibility = 0;
    this.pickPlane.isPickable = true;
    this.pickPlane.scaling.set(BOARD.width * TILE_SIZE, 1, BOARD.depth * TILE_SIZE);
    this.pickPlane.position = this.centre();

    this.pointer = options.scene.onPointerObservable.add((info) => {
      if (!this.active) return;
      if (
        info.type !== PointerEventTypes.POINTERMOVE &&
        info.type !== PointerEventTypes.POINTERPICK
      ) {
        return;
      }
      // A move carries no pick of its own, so the plane has to be asked for one.
      const point =
        info.type === PointerEventTypes.POINTERPICK
          ? info.pickInfo?.pickedMesh === this.pickPlane
            ? info.pickInfo.pickedPoint
            : null
          : (options.scene.pick(
              options.scene.pointerX,
              options.scene.pointerY,
              (mesh) => mesh === this.pickPlane,
            )?.pickedPoint ?? null);
      if (!point) {
        this.clearCandidate();
        return;
      }
      // Resolved from the pick rather than from the last move, so a click decides on
      // where it actually landed and never on a stale hover.
      this.candidate = this.resolveCandidate(point);
      this.drawGhost();
      if (info.type === PointerEventTypes.POINTERPICK) this.commit();
      else this.reportCandidate();
    });

    // On `document` because the viewport is a canvas that is not focusable, so a
    // builder who has not clicked it yet would otherwise find R and M dead.
    this.keys = (event: KeyboardEvent) => {
      if (!this.active) return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      // Defensive rather than reachable today -- this screen has no text field. It
      // stays because the first one it grows, a name for the dungeon, would otherwise
      // spin the block in hand, `room.storeroom` containing both letters.
      if (isTyping(event.target)) return;
      const key = event.key.toLowerCase();
      if (key === 'r') this.turn();
      else if (key === 'm') this.flip();
      else return;
      event.preventDefault();
    };
    document.addEventListener('keydown', this.keys);

    this.bind(options.actions.copy, () => void this.copy());
    this.bind(options.actions.paste, () => void this.paste());
    this.bind(options.actions.reset, () => this.reset());

    this.drawGrid();
    this.restore();
  }

  get state(): BuildState {
    const resolved = resolveLevel(this.level, this.options.catalogue);
    return {
      placed: this.level.blocks.map((block) => ({ ...block })),
      connections: resolved.connections.length,
      sealed: resolved.sealed.length,
      rotation: this.rotation,
      mirrored: this.mirrored,
      selected: this.selected,
      status: this.statusText,
    };
  }

  /** The dungeon as built. The caller may keep it; it is a level document already. */
  get document(): DungeonLevel {
    return this.level;
  }

  /** Which definition the next drop places. */
  take(id: BlockId): void {
    this.selected = id;
    this.palette.select(id);
    this.clearCandidate();
    this.setStatus(`${id} in hand — click the board to place it, R to rotate, M to mirror.`);
  }

  turn(): void {
    this.rotation = ((this.rotation + 90) % 360) as BlockRotation;
    this.afterOrientation();
  }

  flip(): void {
    this.mirrored = !this.mirrored;
    this.afterOrientation();
  }

  /**
   * Puts the level's own JSON on the clipboard.
   *
   * Indented rather than compact: a dungeon handed to somebody else, or kept in a file,
   * is read by a person at least once, and a couple of kilobytes (§10) can afford it.
   */
  async copy(): Promise<void> {
    const count = this.level.blocks.length;
    try {
      await navigator.clipboard.writeText(JSON.stringify(this.level, null, 2));
    } catch (error) {
      console.warn('[build] could not copy the level', error);
      this.setStatus('The browser would not give this page the clipboard, so nothing was copied.');
      return;
    }
    this.setStatus(`Copied ${count} block${count === 1 ? '' : 's'} as JSON.`);
  }

  /**
   * Replaces the board with a level from the clipboard.
   *
   * The text goes straight to `readLevel`, which takes a string and does the `JSON.parse`
   * itself inside its own try/catch -- so a truncated document is refused with the
   * reader's own sentence rather than throwing a `SyntaxError` at a click handler. On any
   * refusal the board is left exactly as it was and the reason is shown verbatim.
   */
  async paste(): Promise<void> {
    let text: string;
    try {
      text = await navigator.clipboard.readText();
    } catch (error) {
      console.warn('[build] could not read the clipboard', error);
      this.setStatus('The browser would not give this page the clipboard, so nothing was pasted.');
      return;
    }
    if (text.trim() === '') {
      this.setStatus('The clipboard is empty.');
      return;
    }

    const read = readLevel(text, this.options.catalogue);
    if (!read.ok) {
      this.setStatus(read.reason);
      return;
    }

    this.adopt(read.level);
    const count = read.level.blocks.length;
    this.setStatus(`Pasted ${count} block${count === 1 ? '' : 's'} — "${read.level.name}".`);
  }

  /**
   * Empties the board, behind a confirm.
   *
   * There is no undo and no removing one block, so the confirm is the only thing
   * between a mis-aimed click and twenty minutes of placing.
   */
  reset(): void {
    const CONFIRM = 'Clear the board? The dungeon you have built will be lost.';
    if (this.level.blocks.length > 0 && !window.confirm(CONFIRM)) return;
    this.adopt(createEmptyLevel());
    this.setStatus('Empty board. Pick a block and click it to place the first one.');
  }

  show(): void {
    this.active = true;
    this.options.root.hidden = false;
    this.pickPlane.setEnabled(true);
    this.grid?.setEnabled(true);
    this.frame();
    this.draw();
  }

  /**
   * Hands the viewport back to the game.
   *
   * The pick plane must stop answering as well as stop being drawn, or a click meant
   * for the dungeon would drop a block on a board nobody can see.
   */
  hide(): void {
    this.active = false;
    this.options.root.hidden = true;
    this.clearCandidate();
    this.pickPlane.setEnabled(false);
    this.grid?.setEnabled(false);
  }

  /** Puts the camera over the whole board rather than over a character. */
  frame(): void {
    const { camera } = this.options;
    camera.setTarget(this.centre());
    camera.alpha = BOARD_FRAMING.alpha;
    camera.beta = BOARD_FRAMING.beta;
    camera.radius = BOARD_FRAMING.radius;
  }

  dispose(): void {
    this.options.scene.onPointerObservable.remove(this.pointer);
    document.removeEventListener('keydown', this.keys);
    for (const { element, handler } of this.clicks) element.removeEventListener('click', handler);
    this.clicks.length = 0;
    this.palette.dispose();
    this.grid?.dispose();
    this.grid = undefined;
    this.pickPlane.dispose();
  }

  /**
   * Where a cell is on screen, in canvas pixels -- the sandbox's `cellScreenPoint` for
   * the same reason: a board is Babylon geometry with no DOM handle, so a test that
   * drops a block where a builder would needs the projection, and the projection needs
   * the camera and the engine.
   */
  cellScreenPoint(cell: Cell): { x: number; y: number } | null {
    const { scene, camera } = this.options;
    const engine = scene.getEngine();
    const world = tileToWorld(cell.x, cell.z);
    const projected = Vector3.Project(
      new Vector3(world.x, 0, world.z),
      Matrix.Identity(),
      scene.getTransformMatrix(),
      camera.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight()),
    );
    if (!Number.isFinite(projected.x) || !Number.isFinite(projected.y)) return null;
    const ratio = engine.getHardwareScalingLevel();
    return { x: projected.x * ratio, y: projected.y * ratio };
  }

  private bind(element: HTMLElement | null, handler: () => void): void {
    if (!element) return;
    element.addEventListener('click', handler);
    this.clicks.push({ element, handler });
  }

  /**
   * Picks up where the last visit left off.
   *
   * **The stored draft goes through `readLevel` like any other document**, because the
   * build that wrote it is not necessarily this one: a draft at `formatVersion` 1, or
   * one naming a block the catalogue has since dropped, is refused with the reader's
   * own sentence rather than half-applied. A refused draft is left in storage rather
   * than cleared -- it is the builder's dungeon, and a reader that improved could open
   * it; the board simply starts empty and the status line says why.
   */
  private restore(): void {
    const read = loadDraft(this.options.catalogue);
    if (!read) {
      this.draw();
      this.setStatus('Pick a block and click the board to place the first one.');
      return;
    }
    if (!read.ok) {
      this.draw();
      this.setStatus(`The saved dungeon could not be opened. ${read.reason}`);
      return;
    }

    this.adopt(read.level, { persist: false });
    const count = read.level.blocks.length;
    this.setStatus(
      count === 0
        ? 'Pick a block and click the board to place the first one.'
        : `${count} block${count === 1 ? '' : 's'} where you left them.`,
    );
  }

  /**
   * Replaces the whole document: a paste, a New, or a restored draft.
   *
   * Nothing else may assign `this.level` wholesale, so there is one place the instance
   * counter is re-seeded and one place a new board is saved from.
   */
  private adopt(level: DungeonLevel, options: { persist?: boolean } = {}): void {
    this.level = level;
    this.instances = level.blocks.length;
    this.clearCandidate();
    this.draw();
    if (options.persist !== false) this.persist();
  }

  private persist(): void {
    saveDraft(this.level);
  }

  /**
   * An instance id no block on the board is already using.
   *
   * The counter alone was enough while every block came from this screen. It is not
   * once a pasted document can arrive with ids of its own, which need not be `blk_<n>`
   * and need not be contiguous: `readLevel` refuses a level whose blocks share an
   * instance id, so a draft saved with a duplicate would be one that never opens again.
   */
  private nextInstanceId(): string {
    const taken = new Set(this.level.blocks.map((block) => block.instanceId));
    let next = this.instances + 1;
    while (taken.has(`blk_${next}`)) next += 1;
    return `blk_${next}`;
  }

  private centre(): Vector3 {
    return new Vector3(((BOARD.width - 1) * TILE_SIZE) / 2, 0, ((BOARD.depth - 1) * TILE_SIZE) / 2);
  }

  private resolveBoard(): ResolvedBlock[] {
    const resolved: ResolvedBlock[] = [];
    for (const block of this.level.blocks) {
      const one = resolvePlacement(block, this.options.catalogue);
      if (one) resolved.push(one);
    }
    return resolved;
  }

  /**
   * The placement a drop at this point would make.
   *
   * Snapping first: every open port on the board is offered to every port of the block
   * in hand, `matingOrigin` says where the block would have to sit for that pair to
   * meet, and the nearest of those to the cursor wins. Nothing within reach means the
   * builder is placing it free, which is a candidate too -- and rule 2 is what refuses
   * it, which is the sentence the status line then shows.
   */
  private resolveCandidate(
    point: Vector3,
  ): { block: PlacedBlock; result: PlacementResult } | null {
    const id = this.selected;
    if (!id) return null;
    const { catalogue } = this.options;
    const definition = catalogue[id];
    if (!definition) return null;

    const oriented = orientBlock(definition, this.rotation, this.mirrored);
    const instanceId = this.nextInstanceId();
    const origins: Cell[] = [];

    for (const open of sealedPorts(this.resolveBoard())) {
      for (let index = 0; index < oriented.ports.length; index += 1) {
        const origin = matingOrigin(oriented, index, open.port);
        if (origin) origins.push(origin);
      }
    }

    const candidates: Cell[] = origins
      .map((origin) => ({ origin, distance: originDistance(origin, point) }))
      .filter((entry) => entry.distance <= SNAP_RADIUS)
      .sort((a, b) => a.distance - b.distance)
      .map((entry) => entry.origin);

    // Where a free drop would sit: the footprint centred under the cursor, so a block
    // lands where it looks as though it will.
    const hovered = worldToTile(point.x, point.z);
    candidates.push({
      x: hovered.x - Math.floor((oriented.footprint.width - 1) / 2),
      z: hovered.z - Math.floor((oriented.footprint.depth - 1) / 2),
    });

    let refused: { block: PlacedBlock; result: PlacementResult } | null = null;
    for (const at of candidates) {
      const block: PlacedBlock = {
        instanceId,
        block: id,
        at,
        rotation: this.rotation,
        ...(this.mirrored ? { mirrored: true } : {}),
      };
      const result = placeBlock(this.level.blocks, block, catalogue);
      if (result.accepted) return { block, result };
      // A snap the rules refuse is not a snap, so keep looking -- but remember the
      // first refusal, which on a board with nothing in reach is the free drop's own.
      refused ??= { block, result };
    }
    return refused;
  }

  private commit(): void {
    const candidate = this.candidate;
    if (!candidate) {
      if (!this.selected) this.setStatus('Pick a block from the palette first.');
      return;
    }
    if (!candidate.result.accepted) {
      this.setStatus(candidate.result.reason);
      return;
    }
    this.level = { ...this.level, blocks: [...this.level.blocks, candidate.block] };
    this.instances += 1;
    this.clearCandidate();
    this.draw();
    this.persist();

    const mated = candidate.result.mated.length;
    const sealed = candidate.result.sealed.length;
    this.setStatus(
      `Placed ${candidate.block.block} — ${mated} connection${mated === 1 ? '' : 's'}, ` +
        `${sealed} port${sealed === 1 ? '' : 's'} sealed.`,
    );
  }

  private afterOrientation(): void {
    this.clearCandidate();
    this.setStatus(
      `${this.selected ?? 'Nothing'} at ${this.rotation}°${this.mirrored ? ', mirrored' : ''}.`,
    );
  }

  private clearCandidate(): void {
    this.candidate = null;
    this.reported = '';
    this.options.view.clearGhost();
  }

  /** Says what a drop here would do, once per message rather than once per mouse event. */
  private reportCandidate(): void {
    const candidate = this.candidate;
    if (!candidate) return;
    const { at } = candidate.block;
    const mated = candidate.result.accepted ? candidate.result.mated.length : 0;
    const message = candidate.result.accepted
      ? `${candidate.block.block} fits at ${at.x},${at.z} — ${mated} connection${mated === 1 ? '' : 's'}.`
      : candidate.result.reason;
    if (message === this.reported) return;
    this.reported = message;
    this.setStatus(message);
  }

  /** The board as it stands, and the three numbers the geometry implies. */
  private draw(): void {
    const resolved = this.options.view.load(this.level, this.options.catalogue);
    const { header } = this.options;
    if (header.blocks) header.blocks.textContent = String(resolved.blocks.length);
    if (header.connections) header.connections.textContent = String(resolved.connections.length);
    if (header.sealed) header.sealed.textContent = String(resolved.sealed.length);
  }

  private drawGhost(): void {
    const candidate = this.candidate;
    if (!candidate) {
      this.options.view.clearGhost();
      return;
    }
    this.options.view.ghost(candidate.block, this.options.catalogue, candidate.result.accepted);
  }

  private setStatus(message: string): void {
    this.statusText = message;
    if (this.options.status) this.options.status.textContent = message;
  }

  private drawGrid(): void {
    this.grid?.dispose();
    const half = TILE_SIZE / 2;
    const minX = -half;
    const minZ = -half;
    const maxX = minX + BOARD.width * TILE_SIZE;
    const maxZ = minZ + BOARD.depth * TILE_SIZE;
    const lines: Vector3[][] = [];

    for (let x = 0; x <= BOARD.width; x += 1) {
      const wx = minX + x * TILE_SIZE;
      lines.push([new Vector3(wx, 0.01, minZ), new Vector3(wx, 0.01, maxZ)]);
    }
    for (let z = 0; z <= BOARD.depth; z += 1) {
      const wz = minZ + z * TILE_SIZE;
      lines.push([new Vector3(minX, 0.01, wz), new Vector3(maxX, 0.01, wz)]);
    }

    this.grid = MeshBuilder.CreateLineSystem('build-grid', { lines }, this.options.scene);
    this.grid.color = GRID_COLOUR;
    this.grid.isPickable = false;
  }
}

/** How far a candidate origin's own first cell is from the cursor. */
function originDistance(origin: Cell, point: Vector3): number {
  const world = tileToWorld(origin.x, origin.z);
  return Math.hypot(world.x - point.x, world.z - point.z);
}

/** True when the keystroke belongs to a field rather than to the board. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
